import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { ISelect, ParamEnum } from '../../../../../shared/types/shared';
import { TableTooltip } from '../../../../components/general/table';
import { DateTooltip } from '../../../../components/general/dateTooltip';
import { useNavigate } from 'react-router-dom';
import { FinalCausalityFilter } from '../../../../../shared/types/typifications/finalCausality';
import { FinalCausalityListActions } from './actions';
import { asyncGetParams } from '../../../../../services/param.service';
import { IParams } from '../../../../../shared/types/redirectParams';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';

export const useFinalCausalityList = (filters?: FinalCausalityFilter) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<IParams[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<FinalCausalityFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<IParams>[] = [];

    if (!isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <FinalCausalityListActions id={row.id} />,
        });
    }

    columns.push(
        {
            field: 'finalCausality',
            headerName: 'Causalidad final',
            minWidth: 150,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <TableTooltip value={(row.row.parent as ISelect)?.name || ''} />
            ),
        },
        {
            field: 'number',
            headerName: 'Número de Causa',
            minWidth: 100,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return params.row.name.split(' - ')[0].trim();
            },
        },
        {
            field: 'realCauseName',
            headerName: 'Nombre de la causal real',
            minWidth: 100,
            flex: 0.5,
            headerAlign: 'center',
            renderCell: (params) => {
                return params.row.name.split(' - ').slice(1).join(' - ').trim();
            },
        },
        {
            field: 'active',
            headerName: 'Activo',
            minWidth: 120,
            flex: 0.4,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <>{row.row.active ? 'Si' : 'No'}</>,
        },
        {
            field: 'created_at',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.6,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <DateTooltip date={row.row.created_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetParams(
            ParamEnum.PQR_FINAL_CAUSALITY_MOTIVE,
            '',
            finalFilters?.finalCausality,
            finalFilters?.causeNumber || finalFilters?.realCause,
            '',
            finalFilters?.active,
            page,
            pageSize
        )
            .then((res: any) => {
                setRows(res?.items);
                setTotal(res?.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        navigate,
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
